import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { DebugWrapper } from '../../pages/Forms/components/DebugWrapper';

/**
 * Nettbank query client
 *
 * The query client works as a cache, and can be used to invalidate queries, e.g. queryClient.invalidateQueries('user')
 *
 * Create other query clients for scoped queries, see documentation for details
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // TODO we should evaluate a default staleTime
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: Infinity, // Disable garbage collection to emulate old behavior before Tanstack Query
      refetchOnWindowFocus: false,
      retry: 1, // Retry failed queries up to 1 time

      // TODO below not really used. Will have more impact if we up the amount of retries
      retryDelay: (attemptIndex) => {
        // Exponential backoff with a base delay of 1000ms and a multiplier of 2
        return Math.min(1000 * 2 ** attemptIndex, 30000); // Cap at 30 seconds
      },
    },
  },
});

// To load ReactQueryDevtools in production, we need to use React.lazy and import it dynamically.
// This is because ReactQueryDevtools is not included in the production build by default.
const ReactQueryDevtoolsProduction = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({
    default: d.ReactQueryDevtools,
  }))
);

export const TanstackQueryClientProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {/* ReactQueryDevtools will only render when process.env.NODE_ENV === 'development'*/}
      {<ReactQueryDevtools />}

      {/* You can use the DEBUG sequence to open ReactQueryDevtools in any environment */}
      <DebugWrapper>
        <React.Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </React.Suspense>
      </DebugWrapper>
    </QueryClientProvider>
  );
};
